<template>
  <v-container fluid class="pa-6">
    <v-row justify="center">
      <v-col cols="12" md="8" lg="6">
        <!-- Header -->
        <div class="form-header mb-6">
          <div class="header-icon">
            <v-icon color="white" size="28">fas fa-lock</v-icon>
          </div>
          <div class="header-text">
            <h2 class="form-title">Change Password</h2>
            <p class="form-subtitle">Update your password to keep your account secure</p>
          </div>
        </div>

        <v-form ref="passwordForm" v-model="isFormValid" lazy-validation>
          <v-alert
            type="info"
            dense
            outlined
            class="mb-4 rule-alert"
          >
            Password rule: minimum 8 characters.
          </v-alert>

          <!-- Current Password -->
          <div class="input-group">
            <label class="input-label">Current Password</label>
            <v-text-field
              v-model="current_password"
              :rules="passwordRules"
              :append-icon="showCurrent ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showCurrent ? 'text' : 'password'"
              @click:append="showCurrent = !showCurrent"
              outlined
              dense
              height="50"
              class="custom-input"
              placeholder="Enter your current password"
              @keyup.enter="focusNext('newPassword')"
              ref="currentPassword"
              autocomplete="current-password"
              :disabled="isLoading"
            ></v-text-field>
          </div>

          <!-- New Password -->
          <div class="input-group">
            <label class="input-label">New Password</label>
            <v-text-field
              v-model="new_password"
              :rules="newPasswordRules"
              :append-icon="showNew ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showNew ? 'text' : 'password'"
              @click:append="showNew = !showNew"
              outlined
              dense
              height="50"
              class="custom-input"
              placeholder="Create a strong password"
              @input="checkPasswordStrength"
              ref="newPassword"
              autocomplete="new-password"
              hint="Use at least 8 characters"
              persistent-hint
              :disabled="isLoading"
            ></v-text-field>

            <!-- Password Strength Indicator -->
            <div v-if="new_password" class="strength-meter mt-2">
              <div class="strength-bar">
                <div
                  class="strength-fill"
                  :style="{ width: strengthPercent + '%', background: strengthColor }"
                ></div>
              </div>
              <span class="strength-text" :style="{ color: strengthColor }">
                {{ strengthText }}
              </span>
            </div>

            <div class="password-hints mt-2">
              <div class="hint-item" :class="{ met: hasLength }">
                <v-icon x-small :color="hasLength ? '#10b981' : '#cbd5e1'">
                  {{ hasLength ? 'fas fa-check' : 'fas fa-circle' }}
                </v-icon>
                <span>At least 8 characters</span>
              </div>
            </div>
          </div>

          <!-- Confirm Password -->
          <div class="input-group">
            <label class="input-label">Confirm New Password</label>
            <v-text-field
              v-model="new_password_again"
              :rules="confirmPasswordRules"
              :append-icon="showConfirm ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showConfirm ? 'text' : 'password'"
              @click:append="showConfirm = !showConfirm"
              outlined
              dense
              height="50"
              class="custom-input"
              placeholder="Re-enter your new password"
              @keyup.enter="updatePassword"
              autocomplete="new-password"
              :disabled="isLoading"
            ></v-text-field>
            <div v-if="passwordsMatch && new_password_again" class="match-indicator">
              <v-icon x-small color="#10b981" class="mr-1">fas fa-check-circle</v-icon>
              <span style="color: #10b981; font-size: 12px;">Passwords match</span>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="action-buttons mt-8">
            <v-btn
              text
              class="cancel-btn mr-3"
              @click="$router.push('/user_profile/account')"
              :disabled="isLoading"
            >
              Cancel
            </v-btn>

            <v-btn
              :disabled="!canSubmit || isLoading"
              :loading="isLoading"
              class="save-btn"
              height="48"
              @click="updatePassword"
            >
              <v-icon left small class="mr-2">fas fa-save</v-icon>
              Update Password
            </v-btn>
          </div>
        </v-form>

        <!-- Security Tips -->
        <v-card class="security-tips mt-8" flat>
          <v-card-text class="pa-4">
            <div class="tips-header">
              <v-icon color="#d97706" small class="mr-2">fas fa-shield-alt</v-icon>
              <span class="tips-title">Security Tips</span>
            </div>
            <ul class="tips-list">
              <li>Use a unique password that you don't use on other sites</li>
              <li>Avoid using personal information like birthdays or names</li>
              <li>Consider using a passphrase (e.g., "Blue-Tiger-Eats-Pizza!")</li>
            </ul>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Success Dialog -->
    <v-dialog v-model="showSuccess" max-width="400" persistent content-class="success-dialog-container">
      <v-card class="success-dialog">
        <div class="success-icon-wrapper">
          <v-icon color="white" size="40">fas fa-check</v-icon>
        </div>
        <v-card-title class="justify-center pt-8 pb-2">
          <span class="success-title">Password Updated!</span>
        </v-card-title>
        <v-card-text class="text-center pb-4">
          <p class="success-text">Your password has been changed successfully. You'll be redirected to your account.</p>
        </v-card-text>
        <v-card-actions class="justify-center pb-6">
          <v-btn color="#1e40af" dark @click="redirectToAccount" class="px-6">
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'

export default {
  name: "SecuritySettings",

  data() {
    return {
      isLoading: false,
      isFormValid: false,
      showCurrent: false,
      showNew: false,
      showConfirm: false,
      showSuccess: false,

      current_password: "",
      new_password: "",
      new_password_again: "",

      // Validation states
      hasLength: false,
      strengthPercent: 0,
      strengthColor: '#cbd5e1',
      strengthText: 'Too weak',

      passwordRules: [
        v => !!v || 'Current password is required',
        v => v.length >= 8 || 'Password must be at least 8 characters'
      ],

      newPasswordRules: [
        v => !!v || 'New password is required',
        v => v.length >= 8 || 'Password must be at least 8 characters'
      ]
    }
  },

  computed: {
    canSubmit() {
      return (
        this.current_password.length > 0 &&
        this.new_password.length >= 8 &&
        this.new_password_again.length > 0 &&
        this.passwordsMatch
      )
    },

    confirmPasswordRules() {
      return [
        v => !!v || 'Please confirm your password',
        v => v === this.new_password || 'Passwords do not match'
      ]
    },

    passwordsMatch() {
      return this.new_password === this.new_password_again && this.new_password.length > 0
    }
  },

  methods: {
    focusNext(refName) {
      this.$refs[refName].focus()
    },

    checkPasswordStrength() {
      const pwd = this.new_password
      this.hasLength = pwd.length >= 8
      this.strengthPercent = Math.min((pwd.length / 12) * 100, 100)

      if (pwd.length < 8) {
        this.strengthColor = '#ef4444'
        this.strengthText = 'Weak'
      } else if (pwd.length < 10) {
        this.strengthColor = '#f59e0b'
        this.strengthText = 'Fair'
      } else if (pwd.length < 14) {
        this.strengthColor = '#3b82f6'
        this.strengthText = 'Good'
      } else {
        this.strengthColor = '#10b981'
        this.strengthText = 'Strong'
      }
    },

    async updatePassword() {
      if (!this.$refs.passwordForm.validate()) return

      this.isLoading = true

      try {
        const response = await axios.post('/api/v1/home/update_account_details/password/', {
          password: this.current_password,
          new_password: this.new_password,
          new_password_again: this.new_password_again
        })

        if (response.data[0].category === "success") {
          this.showSuccess = true
        } else {
          this.showError(response.data[0].text)
        }
      } catch (error) {
        if (error.response) {
          const message = Object.values(error.response.data)[0][0]
          this.showError(message)
        } else {
          this.showError("An error occurred while updating your password")
        }
      } finally {
        this.isLoading = false
      }
    },

    showError(message) {
      this.$store.commit('setSnackBarMessage', message)
      this.$store.commit('setSnackBarColor', "error")
      this.$store.commit('activateSnackBar', true)
    },

    redirectToAccount() {
      this.$router.push('/user_profile/account')
    }
  },

  mounted() {
    this.$store.commit('setCloseURL', "/")
  }
}
</script>

<style scoped>
.form-header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px -4px rgba(30, 58, 138, 0.3);
}

.header-text {
  display: flex;
  flex-direction: column;
}

.form-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
  margin: 0;
}

.form-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 4px 0 0 0;
  font-weight: 500;
}

.input-group {
  margin-bottom: 24px;
}

.rule-alert {
  border-radius: 10px;
}

.input-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.custom-input >>> .v-input__control {
  min-height: 50px;
}

.custom-input >>> .v-input__slot {
  border-radius: 10px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
  transition: all 0.3s ease !important;
}

.custom-input >>> .v-input__slot:hover {
  border-color: #3b82f6 !important;
  box-shadow: 0 4px 12px -2px rgba(59, 130, 246, 0.15) !important;
}

.custom-input.v-input--is-focused >>> .v-input__slot {
  border-color: #1e40af !important;
  box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1) !important;
}

.custom-input >>> .v-label {
  font-size: 14px;
  color: #94a3b8;
  font-weight: 500;
}

.custom-input >>> input {
  font-size: 15px;
  color: #0f172a;
  font-weight: 600;
}

.custom-input >>> .v-input__append-inner {
  margin-top: 0 !important;
}

.custom-input >>> .v-input__append-inner .v-icon {
  font-size: 18px !important;
  color: #64748b !important;
}

.custom-input >>> .v-input__append-inner .v-input__icon--append {
  width: 24px;
  min-width: 24px;
}

.strength-meter {
  display: flex;
  align-items: center;
  gap: 12px;
}

.strength-bar {
  flex: 1;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
}

.strength-fill {
  height: 100%;
  border-radius: 2px;
  transition: all 0.4s ease;
}

.strength-text {
  font-size: 12px;
  font-weight: 700;
  min-width: 50px;
  text-align: right;
  transition: color 0.3s;
}

.password-hints {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.hint-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #94a3b8;
  transition: all 0.3s;
}

.hint-item.met {
  color: #10b981;
  font-weight: 600;
}

.hint-item.met .v-icon {
  font-size: 14px !important;
}

.match-indicator {
  display: flex;
  align-items: center;
  margin-top: 8px;
  animation: fadeIn 0.3s ease;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.cancel-btn {
  color: #64748b;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.5px;
  border-radius: 8px;
  height: 48px;
  padding: 0 24px;
}

.cancel-btn:hover {
  background: #f1f5f9;
  color: #374151;
}

.save-btn {
  background: linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%) !important;
  color: white !important;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0.5px;
  border-radius: 10px;
  padding: 0 28px;
  box-shadow: 0 4px 14px -2px rgba(30, 64, 175, 0.35);
  transition: all 0.3s ease;
}

.save-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px -4px rgba(30, 64, 175, 0.45);
}

.save-btn:disabled {
  background: #cbd5e1 !important;
  color: white !important;
  box-shadow: none;
}

.security-tips {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 1px solid #fcd34d;
  border-radius: 10px;
}

.tips-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.tips-title {
  font-weight: 700;
  color: #92400e;
  font-size: 14px;
}

.tips-list {
  margin: 0;
  padding-left: 20px;
  color: #92400e;
  font-size: 13px;
  line-height: 1.8;
}

.tips-list li {
  margin-bottom: 4px;
}

.success-dialog {
  border-radius: 16px;
  overflow: visible;
  text-align: center;
  margin-top: 42px;
}

.success-icon-wrapper {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  margin: -40px auto 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px -4px rgba(16, 185, 129, 0.4);
  border: 4px solid white;
}

:deep(.success-dialog-container) {
  overflow: visible;
}

:deep(.success-dialog-container .v-dialog) {
  overflow: visible;
}

.success-title {
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
}

.success-text {
  color: #64748b;
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 600px) {
  .form-header {
    flex-direction: column;
    text-align: center;
  }

  .action-buttons {
    flex-direction: column-reverse;
    gap: 12px;
    position: sticky;
    bottom: 0;
    background: #fff;
    padding-top: 10px;
    z-index: 2;
  }

  .cancel-btn, .save-btn {
    width: 100%;
    margin: 0;
  }
}
</style>